// Generates a "poster hanging on a living-room wall" picture (or a laptop-lid
// picture for sticker sheets) entirely in the browser — no external service.
const W = 1600;
const H = 1200;

// small seeded random so the same poster always gets the same room
const rng = (seed) => {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    return (s % 100000) / 100000;
  };
};

const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const shade = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + amt)));
  const r = f((n >> 16) & 255);
  const g = f((n >> 8) & 255);
  const b = f(n & 255);
  return `rgb(${r},${g},${b})`;
};

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const ROOMS = [
  { wall: '#d8d0c4', sofa: '#46525e', floor: '#a47a52', accent: '#7d9a6a', rug: '#b8aa98' },
  { wall: '#c7ccd1', sofa: '#8a5a3a', floor: '#b08a62', accent: '#5f8a7a', rug: '#aab2b8' },
  { wall: '#e6dacc', sofa: '#2f3a46', floor: '#9a7048', accent: '#a0804f', rug: '#cdbfae' },
  { wall: '#cdd6cb', sofa: '#6b4f43', floor: '#a98760', accent: '#5e7d57', rug: '#b5beb2' },
  { wall: '#2c3038', sofa: '#bba88f', floor: '#5d4636', accent: '#c9a45c', rug: '#454a54' },
  { wall: '#e2cfc9', sofa: '#4b4f5e', floor: '#b28f6c', accent: '#8a9e86', rug: '#cdb7b0' },
];

// 0 = dark picture, 1 = bright picture
function brightness(img) {
  const c = document.createElement('canvas');
  c.width = 24;
  c.height = 24;
  const x = c.getContext('2d');
  x.drawImage(img, 0, 0, 24, 24);
  const d = x.getImageData(0, 0, 24, 24).data;
  let sum = 0;
  for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  return sum / (d.length / 4) / 255;
}

function drawWall(ctx, room, rand) {
  const wallH = H * 0.84;
  ctx.fillStyle = room.wall;
  ctx.fillRect(0, 0, W, H);
  // soft light from the top
  const g = ctx.createRadialGradient(W * 0.5, H * 0.05, 40, W * 0.5, H * 0.35, W * 0.85);
  g.addColorStop(0, 'rgba(255,255,255,0.28)');
  g.addColorStop(1, 'rgba(0,0,0,0.20)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, wallH);
  // plaster grain
  for (let i = 0; i < 2600; i += 1) {
    ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)';
    ctx.fillRect(rand() * W, rand() * wallH, 2 + rand() * 6, 1 + rand() * 3);
  }
  // skirting board
  ctx.fillStyle = shade(room.wall.length === 7 ? room.wall : '#dddddd', 18);
  ctx.fillRect(0, wallH - 26, W, 26);
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.fillRect(0, wallH - 2, W, 4);
  // floor
  const fg = ctx.createLinearGradient(0, wallH, 0, H);
  fg.addColorStop(0, shade(room.floor, -28));
  fg.addColorStop(1, shade(room.floor, 14));
  ctx.fillStyle = fg;
  ctx.fillRect(0, wallH, W, H - wallH);
  ctx.strokeStyle = 'rgba(0,0,0,0.13)';
  ctx.lineWidth = 2;
  for (let i = 1; i < 9; i += 1) {
    const y = wallH + ((H - wallH) * i) / 9;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  return wallH;
}

function drawPlant(ctx, cx, baseY, room, scale = 1) {
  // pot
  ctx.fillStyle = '#e9e4da';
  ctx.beginPath();
  ctx.moveTo(cx - 34 * scale, baseY - 70 * scale);
  ctx.lineTo(cx + 34 * scale, baseY - 70 * scale);
  ctx.lineTo(cx + 26 * scale, baseY);
  ctx.lineTo(cx - 26 * scale, baseY);
  ctx.closePath();
  ctx.fill();
  // leaves
  ctx.fillStyle = shade(room.accent, -25);
  for (let i = 0; i < 9; i += 1) {
    const a = (-Math.PI / 2) + (i - 4) * 0.34;
    ctx.save();
    ctx.translate(cx, baseY - 66 * scale);
    ctx.rotate(a + Math.PI / 2);
    ctx.beginPath();
    ctx.ellipse(0, -80 * scale - (i % 3) * 14 * scale, 20 * scale, 78 * scale, 0, 0, Math.PI * 2);
    ctx.fillStyle = i % 2 ? shade(room.accent, -30) : shade(room.accent, 0);
    ctx.fill();
    ctx.restore();
  }
}

function drawSofa(ctx, room, wallH) {
  const sw = W * 0.64;
  const sx = (W - sw) / 2;
  const top = H * 0.615;
  const baseY = wallH + 20;
  // rug
  ctx.fillStyle = room.rug;
  ctx.beginPath();
  ctx.ellipse(W / 2, baseY + 70, sw * 0.62, 70, 0, 0, Math.PI * 2);
  ctx.fill();
  // shadow
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.beginPath();
  ctx.ellipse(W / 2, baseY + 10, sw * 0.52, 22, 0, 0, Math.PI * 2);
  ctx.fill();
  // legs
  ctx.fillStyle = '#2a2018';
  [sx + 40, sx + sw - 52].forEach((lx) => ctx.fillRect(lx, baseY - 26, 12, 30));
  // back
  const back = ctx.createLinearGradient(0, top, 0, baseY);
  back.addColorStop(0, shade(room.sofa, 22));
  back.addColorStop(1, shade(room.sofa, -12));
  ctx.fillStyle = back;
  rr(ctx, sx + 30, top, sw - 60, baseY - top - 60, 34);
  ctx.fill();
  // arms
  ctx.fillStyle = shade(room.sofa, -8);
  rr(ctx, sx, top + 40, 78, baseY - top - 28, 30);
  ctx.fill();
  rr(ctx, sx + sw - 78, top + 40, 78, baseY - top - 28, 30);
  ctx.fill();
  // seat cushions
  const seatTop = baseY - 112;
  const cw = (sw - 156) / 3;
  for (let i = 0; i < 3; i += 1) {
    const cg = ctx.createLinearGradient(0, seatTop, 0, seatTop + 90);
    cg.addColorStop(0, shade(room.sofa, 30));
    cg.addColorStop(1, shade(room.sofa, -6));
    ctx.fillStyle = cg;
    rr(ctx, sx + 78 + i * cw + 3, seatTop, cw - 6, 92, 22);
    ctx.fill();
  }
  // throw pillows
  ctx.fillStyle = room.accent;
  ctx.save();
  ctx.translate(sx + 150, seatTop - 22);
  ctx.rotate(-0.12);
  rr(ctx, -42, -42, 84, 84, 16);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = shade(room.accent, 40);
  ctx.save();
  ctx.translate(sx + sw - 160, seatTop - 18);
  ctx.rotate(0.14);
  rr(ctx, -40, -40, 80, 80, 16);
  ctx.fill();
  ctx.restore();
}

function drawFrame(ctx, img, cx, cy, boxW, boxH, frameColor) {
  const ar = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
  let w = boxW;
  let h = w / ar;
  if (h > boxH) {
    h = boxH;
    w = h * ar;
  }
  const t = Math.max(8, Math.min(16, w * 0.025));
  const x = cx - w / 2;
  const y = cy - h / 2;
  // nail + soft shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.42)';
  ctx.shadowBlur = 38;
  ctx.shadowOffsetX = 10;
  ctx.shadowOffsetY = 24;
  ctx.fillStyle = frameColor;
  ctx.fillRect(x - t, y - t, w + t * 2, h + t * 2);
  ctx.restore();
  // frame bevel
  ctx.fillStyle = frameColor;
  ctx.fillRect(x - t, y - t, w + t * 2, h + t * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  ctx.fillRect(x - t, y - t, w + t * 2, 2);
  ctx.fillRect(x - t, y - t, 2, h + t * 2);
  // poster
  ctx.drawImage(img, x, y, w, h);
  // inner shadow where the frame overlaps
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 3;
  ctx.strokeRect(x, y, w, h);
  // glass shine
  const gl = ctx.createLinearGradient(x, y, x + w, y + h);
  gl.addColorStop(0, 'rgba(255,255,255,0.16)');
  gl.addColorStop(0.35, 'rgba(255,255,255,0.02)');
  gl.addColorStop(0.5, 'rgba(255,255,255,0)');
  gl.addColorStop(1, 'rgba(255,255,255,0.06)');
  ctx.fillStyle = gl;
  ctx.fillRect(x, y, w, h);
}

function wallScene(ctx, img, seed) {
  const rand = rng(seed);
  const bright = brightness(img);
  // dark pictures look best on lighter walls; bright ones on any wall but the dark one
  const pool = bright < 0.35 ? ROOMS.filter((r) => r.wall !== '#2c3038') : ROOMS;
  const room = pool[seed % pool.length];
  const wallH = drawWall(ctx, room, rand);

  // floor lamp (left) and plant (right)
  ctx.strokeStyle = '#2a2a2a';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(W * 0.1, wallH + 10);
  ctx.lineTo(W * 0.1, H * 0.42);
  ctx.stroke();
  ctx.fillStyle = '#2a2a2a';
  ctx.beginPath();
  ctx.ellipse(W * 0.1, wallH + 10, 34, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  const lampG = ctx.createRadialGradient(W * 0.1, H * 0.4, 6, W * 0.1, H * 0.4, 230);
  lampG.addColorStop(0, 'rgba(255,226,160,0.50)');
  lampG.addColorStop(1, 'rgba(255,226,160,0)');
  ctx.fillStyle = lampG;
  ctx.fillRect(0, 0, W * 0.4, H * 0.8);
  ctx.fillStyle = '#f1e2bd';
  ctx.beginPath();
  ctx.moveTo(W * 0.1 - 46, H * 0.42);
  ctx.lineTo(W * 0.1 + 46, H * 0.42);
  ctx.lineTo(W * 0.1 + 30, H * 0.35);
  ctx.lineTo(W * 0.1 - 30, H * 0.35);
  ctx.closePath();
  ctx.fill();

  drawPlant(ctx, W * 0.9, wallH + 6, room, 1.5);
  drawSofa(ctx, room, wallH);

  const ar = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
  const wide = ar > 1.15;
  const boxW = wide ? W * 0.52 : W * 0.30;
  const boxH = H * 0.40;
  const frameColor = room.wall === '#2c3038' ? '#d9d2c4' : '#17171a';
  drawFrame(ctx, img, W / 2, H * 0.325, boxW, boxH, frameColor);

  // gentle vignette
  const v = ctx.createRadialGradient(W / 2, H / 2, H * 0.45, W / 2, H / 2, W * 0.72);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, W, H);
}

function laptopScene(ctx, img, seed) {
  const rand = rng(seed);
  const room = ROOMS[seed % 4];
  const wallH = H * 0.74;
  ctx.fillStyle = room.wall;
  ctx.fillRect(0, 0, W, H);
  const g = ctx.createRadialGradient(W * 0.5, H * 0.1, 40, W * 0.5, H * 0.4, W * 0.8);
  g.addColorStop(0, 'rgba(255,255,255,0.30)');
  g.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, wallH);
  for (let i = 0; i < 1800; i += 1) {
    ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)';
    ctx.fillRect(rand() * W, rand() * wallH, 2 + rand() * 6, 1 + rand() * 3);
  }
  // desk
  const dg = ctx.createLinearGradient(0, wallH, 0, H);
  dg.addColorStop(0, shade(room.floor, -10));
  dg.addColorStop(1, shade(room.floor, 24));
  ctx.fillStyle = dg;
  ctx.fillRect(0, wallH, W, H - wallH);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(0, wallH, W, 5);

  // laptop lid, seen from behind
  const lw = W * 0.56;
  const lh = lw * 0.64;
  const lx = (W - lw) / 2;
  const ly = wallH - lh + 30;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.4)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 26;
  ctx.fillStyle = '#b9bcc2';
  rr(ctx, lx, ly, lw, lh, 26);
  ctx.fill();
  ctx.restore();
  const lg = ctx.createLinearGradient(lx, ly, lx + lw, ly + lh);
  lg.addColorStop(0, '#d5d8dd');
  lg.addColorStop(1, '#a4a8af');
  ctx.fillStyle = lg;
  rr(ctx, lx, ly, lw, lh, 26);
  ctx.fill();
  // stickers
  const ar = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
  let sw = lw * 0.86;
  let sh = sw / ar;
  if (sh > lh * 0.86) {
    sh = lh * 0.86;
    sw = sh * ar;
  }
  ctx.save();
  ctx.beginPath();
  rr(ctx, lx + 14, ly + 14, lw - 28, lh - 28, 18);
  ctx.clip();
  ctx.drawImage(img, lx + (lw - sw) / 2, ly + (lh - sh) / 2, sw, sh);
  const shine = ctx.createLinearGradient(lx, ly, lx + lw, ly + lh);
  shine.addColorStop(0, 'rgba(255,255,255,0.20)');
  shine.addColorStop(0.45, 'rgba(255,255,255,0)');
  shine.addColorStop(1, 'rgba(255,255,255,0.08)');
  ctx.fillStyle = shine;
  ctx.fillRect(lx, ly, lw, lh);
  ctx.restore();
  // hinge/base
  ctx.fillStyle = '#8e9299';
  rr(ctx, lx - 40, wallH + 8, lw + 80, 22, 11);
  ctx.fill();
  // mug + plant
  ctx.fillStyle = '#f2efe9';
  rr(ctx, W * 0.80, wallH - 64, 74, 76, 12);
  ctx.fill();
  ctx.fillStyle = '#4a2f22';
  ctx.fillRect(W * 0.80 + 6, wallH - 58, 62, 10);
  ctx.strokeStyle = '#f2efe9';
  ctx.lineWidth = 9;
  ctx.beginPath();
  ctx.arc(W * 0.80 + 78, wallH - 28, 18, -Math.PI / 2, Math.PI / 2);
  ctx.stroke();
  drawPlant(ctx, W * 0.12, wallH + 8, room, 1.1);

  const v = ctx.createRadialGradient(W / 2, H / 2, H * 0.45, W / 2, H / 2, W * 0.72);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, W, H);
}

/**
 * @param {CanvasImageSource} img  decoded poster
 * @param {{key:string, laptop?:boolean}} opts  key keeps the room the same for the same poster
 * @returns {Promise<{blob:Blob, ext:string}>}
 */
export async function makeMockup(img, { key = 'x', laptop = false } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  const seed = hashSeed(key);
  if (laptop) laptopScene(ctx, img, seed);
  else wallScene(ctx, img, seed);
  const toBlob = (t, q) => new Promise((res) => canvas.toBlob(res, t, q));
  let blob = await toBlob('image/webp', 0.86);
  let ext = 'webp';
  if (!blob || blob.type !== 'image/webp') {
    blob = await toBlob('image/jpeg', 0.9);
    ext = 'jpg';
  }
  return { blob, ext };
}
